import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { Box, Typography } from '@mui/material';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $getRoot, $createParagraphNode, type EditorState, type LexicalEditor } from 'lexical';
import { $generateHtmlFromNodes, $generateNodesFromDOM } from '@lexical/html';
import { HeadingNode, QuoteNode } from '@lexical/rich-text';
import { ListItemNode, ListNode } from '@lexical/list';
import { LinkNode } from '@lexical/link';
import { CheckListPlugin } from '@lexical/react/LexicalCheckListPlugin';
import { useThemeContext } from '../../../hook/useThemeManager';
import { theme } from '../../../constants/theme';
import { FONT_ENUM } from '../../../types/global/fonts.types';
import { BRAND_LUXE_PRIMARY } from '../../../pages/BrandDashboard/brandDashboardTokens';
import {
    normalizeRichTextForEditor,
    plainTextFromHtml,
} from '../../../pages/BrandDashboard/contestWizard/richTextUtils';
import type { RichTextEditorProps } from './types';
import { LexicalToolbar } from './LexicalToolbar';
import { ImageNode } from './ImageNode';

const editorTheme = {
    paragraph: 'thspian-lexical-p',
    quote: 'thspian-lexical-quote',
    heading: {
        h1: 'thspian-lexical-h1',
        h2: 'thspian-lexical-h2',
        h3: 'thspian-lexical-h3',
    },
    list: {
        ul: 'thspian-lexical-ul',
        ol: 'thspian-lexical-ol',
        listitem: 'thspian-lexical-li',
        listitemChecked: 'thspian-lexical-li-checked',
        listitemUnchecked: 'thspian-lexical-li-unchecked',
        nested: {
            listitem: 'thspian-lexical-nested-li',
        },
        check: 'thspian-lexical-ul-check',
    },
    link: 'thspian-lexical-link',
    text: {
        bold: 'thspian-lexical-bold',
        italic: 'thspian-lexical-italic',
        underline: 'thspian-lexical-underline',
        strikethrough: 'thspian-lexical-strike',
    },
};

function htmlFromEditor(editor: LexicalEditor): string {
    let html = '';
    editor.getEditorState().read(() => {
        html = $generateHtmlFromNodes(editor, null);
    });
    return html;
}

function setEditorHtml(editor: LexicalEditor, html: string) {
    editor.update(() => {
        const root = $getRoot();
        root.clear();
        const normalized = normalizeRichTextForEditor(html).trim();
        if (!normalized) {
            root.append($createParagraphNode());
            return;
        }
        const parser = new DOMParser();
        const dom = parser.parseFromString(normalized, 'text/html');
        const nodes = $generateNodesFromDOM(editor, dom.body);
        if (nodes.length) {
            root.append(...nodes);
        } else {
            root.append($createParagraphNode());
        }
    });
}

/** Keep Lexical in sync when parent value changes (draft restore, AI assist, etc.). */
function HtmlValuePlugin({ value }: { value: string }) {
    const [editor] = useLexicalComposerContext();
    const lastApplied = useRef<string | null>(null);

    useEffect(() => {
        const next = normalizeRichTextForEditor(value || '');
        const current = htmlFromEditor(editor);
        const nextPlain = plainTextFromHtml(next);
        const currentPlain = plainTextFromHtml(current);
        if (next === lastApplied.current) return;
        if (next === current || (nextPlain === currentPlain && nextPlain === '')) {
            lastApplied.current = next;
            return;
        }
        lastApplied.current = next;
        setEditorHtml(editor, next);
    }, [editor, value]);

    return null;
}

export const LexicalRichTextEditor: React.FC<RichTextEditorProps> = ({
    value,
    onChange,
    label,
    labelSx,
    placeholder = 'Write something…',
    minChar,
    shellVariant = 'default',
    compact = false,
    fillContainer = false,
}) => {
    const { themeStyle } = useThemeContext();
    const lastEmitted = useRef('');

    const initialConfig = useMemo(
        () => ({
            namespace: `ThspianRichText-${shellVariant}`,
            theme: editorTheme,
            onError: (error: Error) => {
                // eslint-disable-next-line no-console
                console.error(error);
            },
            nodes: [HeadingNode, QuoteNode, ListNode, ListItemNode, LinkNode, ImageNode],
        }),
        [shellVariant],
    );

    const handleChange = useCallback(
        (_editorState: EditorState, editor: LexicalEditor) => {
            const html = htmlFromEditor(editor);
            if (html === lastEmitted.current) return;
            lastEmitted.current = html;
            onChange(html);
        },
        [onChange],
    );

    const characterCount = plainTextFromHtml(value || '').length;
    const isMinCharMet = minChar ? characterCount >= minChar : true;

    const isBrandLike = shellVariant === 'brandDashboard' || shellVariant === 'companyAbout';
    const isJury = shellVariant === 'juryDuty';

    const shellSx = {
        position: 'relative' as const,
        width: '100%',
        display: 'flex',
        flexDirection: 'column' as const,
        borderRadius: isJury ? '12px' : '8px',
        overflow: 'hidden',
        border: `1px solid ${isBrandLike || isJury ? 'rgba(255,255,255,0.1)' : theme[themeStyle].main_blue}`,
        bgcolor: isJury ? '#1A1A1A' : isBrandLike ? '#1c1c1c' : theme[themeStyle].secondaryBg,
        ...(fillContainer ? { height: '100%', minHeight: 0, flex: 1 } : {}),
        '&:focus-within': isBrandLike
            ? {
                  borderColor: `${BRAND_LUXE_PRIMARY}88`,
                  boxShadow: '0 0 0 1px rgba(236,170,0,0.25)',
              }
            : {},
        '& .thspian-lexical-bold': { fontFamily: FONT_ENUM.MAIN_BOLD, fontWeight: 700 },
        '& .thspian-lexical-italic': { fontStyle: 'italic' },
        '& .thspian-lexical-underline': { textDecoration: 'underline' },
        '& .thspian-lexical-link': { color: BRAND_LUXE_PRIMARY, textDecoration: 'underline' },
        '& .thspian-lexical-ul, & .thspian-lexical-ol, & .thspian-lexical-ul-check': { pl: 3, my: 0.5 },
        '& .thspian-lexical-li-checked, & .thspian-lexical-li-unchecked': {
            position: 'relative',
            marginLeft: '8px',
            paddingLeft: '24px',
            listStyleType: 'none',
            outline: 'none',
        },
        '& .thspian-lexical-li-checked': {
            textDecoration: 'line-through',
            opacity: 0.7,
        },
        '& .thspian-lexical-li-unchecked:before, & .thspian-lexical-li-checked:before': {
            content: '""',
            width: '16px',
            height: '16px',
            position: 'absolute',
            left: 0,
            top: '4px',
            cursor: 'pointer',
            display: 'block',
            backgroundSize: 'cover',
            border: '1px solid rgba(255,255,255,0.35)',
            borderRadius: '3px',
        },
        '& .thspian-lexical-li-checked:before': {
            bgcolor: BRAND_LUXE_PRIMARY,
            borderColor: BRAND_LUXE_PRIMARY,
        },
        '& .thspian-lexical-image-wrap': { display: 'block' },
        '& .thspian-lexical-p': { m: 0, mb: 0.75 },
        '& .thspian-lexical-h1': { fontSize: '1.5rem', fontFamily: FONT_ENUM.MAIN_BOLD, m: 0, mb: 0.75 },
        '& .thspian-lexical-h2': { fontSize: '1.25rem', fontFamily: FONT_ENUM.MAIN_BOLD, m: 0, mb: 0.75 },
        '& .thspian-lexical-h3': { fontSize: '1.1rem', fontFamily: FONT_ENUM.MAIN_BOLD, m: 0, mb: 0.75 },
        '& .thspian-lexical-quote': {
            borderLeft: `3px solid ${BRAND_LUXE_PRIMARY}`,
            pl: 1.5,
            color: 'rgba(255,255,255,0.7)',
            my: 1,
        },
    };

    const minEditorHeight = fillContainer ? 48 : compact ? 72 : isBrandLike ? 160 : 140;

    return (
        <Box sx={shellSx}>
            {!!label && (
                <Typography
                    sx={{
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        color: theme[themeStyle].tag_white,
                        fontSize: 12,
                        mb: shellVariant === 'brandDashboard' ? 1 : 2,
                        ...labelSx,
                    }}>
                    {label}
                </Typography>
            )}
            <LexicalComposer initialConfig={initialConfig}>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        flex: fillContainer ? 1 : undefined,
                        minHeight: 0,
                        overflow: 'hidden',
                    }}>
                    <LexicalToolbar compact={compact || isJury} />
                    <Box sx={{ position: 'relative', flex: fillContainer ? 1 : undefined, minHeight: 0 }}>
                        <RichTextPlugin
                            contentEditable={
                                <ContentEditable
                                    aria-placeholder={placeholder}
                                    placeholder={
                                        <Box
                                            sx={{
                                                position: 'absolute',
                                                top: compact ? 10 : 14,
                                                left: 14,
                                                right: 14,
                                                color: 'rgba(255,255,255,0.35)',
                                                pointerEvents: 'none',
                                                fontFamily: FONT_ENUM.MEDIUM_TEXT,
                                                fontSize: 14,
                                                lineHeight: 1.6,
                                            }}>
                                            {placeholder}
                                        </Box>
                                    }
                                    style={{
                                        minHeight: minEditorHeight,
                                        maxHeight: compact && !fillContainer ? 120 : undefined,
                                        outline: 'none',
                                        padding: compact ? '10px 12px' : '14px',
                                        color: '#f5f5f5',
                                        fontFamily: FONT_ENUM.MEDIUM_TEXT,
                                        fontSize: 14,
                                        lineHeight: 1.6,
                                        overflowY: 'auto',
                                        background: isJury ? '#121212' : isBrandLike ? '#262626' : undefined,
                                    }}
                                />
                            }
                            ErrorBoundary={LexicalErrorBoundary}
                        />
                    </Box>
                    <HistoryPlugin />
                    <ListPlugin />
                    <CheckListPlugin />
                    <LinkPlugin />
                    <HtmlValuePlugin value={value} />
                    <OnChangePlugin onChange={handleChange} ignoreSelectionChange />
                </Box>
            </LexicalComposer>
            {minChar ? (
                <Typography
                    sx={{
                        fontSize: 11,
                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                        px: 1.5,
                        py: 1,
                        borderTop: '1px solid rgba(255,255,255,0.06)',
                        color: isMinCharMet
                            ? theme[themeStyle].green_status_indicator_full
                            : theme[themeStyle].primary_red,
                    }}>
                    Min char. {characterCount}/{minChar}
                </Typography>
            ) : null}
        </Box>
    );
};
